import { batch, createEffect, createMemo, onCleanup, untrack } from "solid-js"
import { createStore, reconcile } from "solid-js/store"
import { useGlobal } from "@/context/global"
import { useLanguage } from "@/context/language"
import { useServer } from "@/context/server"
import { listSessions } from "@/datox/api"
import { getWorkflowApplication } from "@/datox/automations"
import { workflowChatIDs, workflowProcessingStatus, workflowSessionActivity, workflowSessionIDs, workflowStatusEvent, workflowStatusSnapshot, type WorkflowScope, type WorkflowSessionStatus } from "@/datox/workflow-status"
import { workflowResultTabs, workflowSavedResultEvent, workflowTabsForStage, type WorkflowResultTab } from "@/datox/workflow-tab-status"

export function DatoxWorkflowStatus(props: WorkflowScope & { onResultsSaved?: (tabs: readonly WorkflowResultTab[]) => void; onActivity?: (tabs: WorkflowResultTab[]) => void }) {
  const language = useLanguage()
  const server = useServer()
  const global = useGlobal()
  const url = import.meta.env.VITE_DATOX_OPENCODE_SERVER_URL || ""
  const connection = server.list.find((item) => item.type === "http" && item.http.url.replace(/\/+$/, "") === url.replace(/\/+$/, ""))
    ?? server.add({ type: "http", http: { url, username: import.meta.env.VITE_DATOX_OPENCODE_USERNAME || "opencode", password: import.meta.env.VITE_DATOX_OPENCODE_PASSWORD || "" }, displayName: language.t("datox.workflows.chat.regularAgent") })
  const runtime = connection ? global.ensureServerCtx(connection) : undefined
  const [state, setState] = createStore({
    loaded: false,
    error: false,
    roots: [] as string[],
    snapshot: {} as Record<string, WorkflowSessionStatus>,
    snapshotRevision: 0,
    events: {} as Record<string, { status: WorkflowSessionStatus; revision: number }>,
    automationStatus: undefined as string | undefined,
  })

  createEffect(() => {
    const scope = { projectID: props.projectID, periodID: props.periodID, caseID: props.caseID }
    setState(reconcile({ loaded: false, error: !runtime, roots: [], snapshot: {}, snapshotRevision: 0, events: {}, automationStatus: undefined }))
    if (!runtime) return
    let disposed = false
    let fetching = false
    let revision = 0
    const saved = new Set<string>()
    const refresh = async () => {
      if (disposed || fetching || document.hidden) return
      fetching = true
      const started = revision
      try {
        const [sessions, statuses, application] = await Promise.all([
          listSessions(scope.projectID, scope.periodID),
          runtime.sdk.protocol.then(async (protocol): Promise<Record<string, WorkflowSessionStatus>> => {
            if (protocol === "v1") {
              const result = await runtime.sdk.client.session.status()
              return Object.fromEntries(Object.entries(result.data ?? {}).map(([id, status]) => [id, status.type]))
            }
            const active = await runtime.sdk.api.session.active()
            return Object.fromEntries(Object.keys(active).map((id) => [id, "busy"]))
          }),
          scope.periodID ? getWorkflowApplication(scope.projectID, scope.periodID, scope.caseID) : undefined,
        ])
        if (disposed) return
        batch(() => {
          setState("snapshot", reconcile(statuses))
          setState({
            loaded: true, error: false, roots: workflowChatIDs(sessions, scope), snapshotRevision: started,
            automationStatus: application?.latest_run?.status ?? application?.application?.latest_run?.status,
          })
        })
      } catch {
        if (!disposed) setState("error", true)
      } finally {
        fetching = false
      }
    }
    const unsubscribe = runtime.sdk.event.listen(({ details }) => {
      const update = workflowStatusEvent(details)
      if (update) setState("events", update.sessionID, { status: update.status, revision: ++revision })
      const result = workflowSavedResultEvent(details, runtime.sync.session.data.part)
      if (result && !saved.has(result.key) && workflowSessionIDs(state.roots, [result.sessionID], runtime.sync.session.data.info).includes(result.sessionID)) {
        saved.add(result.key)
        props.onResultsSaved?.(result.tabs)
      }
      if (details.type === "server.connected") {
        void refresh()
        if (state.loaded) props.onResultsSaved?.(workflowResultTabs)
      }
    })
    const timer = setInterval(() => void refresh(), 10_000)
    const resume = () => void refresh()
    window.addEventListener("focus", resume)
    document.addEventListener("visibilitychange", resume)
    void refresh()
    onCleanup(() => {
      disposed = true
      unsubscribe()
      clearInterval(timer)
      window.removeEventListener("focus", resume)
      document.removeEventListener("visibilitychange", resume)
    })
  })

  // A snapshot requested before an event must not overwrite that newer live status.
  const statuses = createMemo(() => workflowStatusSnapshot(state.snapshot, state.snapshotRevision, state.events))
  const activeSessions = createMemo(() => {
    if (!runtime || !state.loaded || state.error) return []
    const sessions = runtime.sync.session.data
    return workflowSessionIDs(state.roots, Object.keys(statuses()), sessions.info).filter((id) =>
      statuses()[id] === "busy" && !sessions.question[id]?.length && !sessions.permission[id]?.length)
  })
  const hydrated = new Set<string>()
  createEffect(() => {
    for (const id of activeSessions()) {
      if (!runtime || hydrated.has(id)) continue
      hydrated.add(id)
      // Reuse the chat cache and hydrate only a small recent page for active sessions.
      void runtime.sync.session.sync(id, { messageLimit: 5 }).catch(() => hydrated.delete(id))
    }
  })

  const phase = createMemo(() => {
    if (state.error || !runtime) return "unavailable"
    if (!state.loaded) return "loading"
    const sessions = runtime.sync.session.data
    return workflowProcessingStatus({
      roots: state.roots, statuses: statuses(), parents: sessions.info, automationStatus: state.automationStatus,
      waiting: (id) => !!sessions.question[id]?.length || !!sessions.permission[id]?.length,
    })
  })

  const label = createMemo(() => {
    if (phase() !== "processing" || !runtime) return phase()
    const sessions = runtime.sync.session.data
    const activities = activeSessions().flatMap((id) => {
      const activity = workflowSessionActivity(sessions.message[id] ?? [], sessions.part)
      return activity ? [activity] : []
    }).sort((a, b) => b.priority - a.priority || b.time - a.time)
    return activities[0]?.stage ?? "processing"
  })

  let previousPhase = "loading"
  createEffect(() => {
    const next = phase()
    if (["idle", "failed", "interrupted", "cancelled"].includes(next) && ["processing", "retrying", "waiting", "cancelling"].includes(previousPhase)) untrack(() => props.onResultsSaved?.(workflowResultTabs))
    previousPhase = next
    const tabs = next === "processing" ? workflowTabsForStage(label()) : []
    untrack(() => props.onActivity?.(tabs))
  })
  onCleanup(() => props.onActivity?.([]))

  return <div class="workflow-processing-status" data-state={phase()} role="status" aria-live="polite" aria-atomic="true">
    <span class="workflow-processing-status__indicator" aria-hidden="true" />
    <span>{language.t(`datox.workflow.processing.${label()}`)}</span>
  </div>
}
